import { Anchor } from "@svea/design-system-components-react";
import {
	Divider,
	formatOrgOrPersonNumber,
	formatSniCode,
	formatSsn,
	Grid,
	Heading,
	Li,
	Markdown,
	moneyFormatNoDecimal,
	P,
	Stack,
	SvgIcon,
	Text,
	Ul,
	useText,
} from "@sveabank/cc-common";
import { Link } from "react-router";
import Card from "../../components/card";
import Content from "../../components/content";
import { withLoader } from "../../hoc/with-loader";
import { usePageContext } from "../../hooks/context";
import { useClcCompanyApplication } from "./hooks";
import ClcCardSummary from "./steps/agreement/card-summary";
import {
	formatNumericValue,
	getIsPersonShareholder,
	getTotalAddonCost,
	getTotalDistributedShares,
} from "./utils";

const ClcSummaryPage = () => {
	const {
		clcApplicationDetails: {
			applicant,
			beneficialOwners,
			companyRegistration: { value: companyRegistration },
			companyShares,
			representatives,
			organisationInfo: { value: organisationInfo },
		},
		selectedPackage,
		cards: {
			debitCards: { cards },
		},
		controlTypeMap,
		roles,
		allSniCodes,
		signatoryCombinations,
	} = usePageContext(useClcCompanyApplication);

	const [t, texts] = useText();

	const packages = texts.newCompany.packages;
	const text = packages[selectedPackage?.packageId];

	const signatoryCombinationDescription =
		representatives.value?.signatoryCombination.signatoryCombination === "Other"
			? representatives.value.signatoryCombination.details
			: signatoryCombinations
					.find((s) => s.value === representatives.value?.signatoryCombination.signatoryCombination)
					?.labels.find((label) => label.language === "sv")?.label || "";

	const companyName =
		organisationInfo?.orgName || companyRegistration?.proposedCompanyNames[0] || "";

	return (
		<Content py={4}>
			<Stack gap={8}>
				<Heading.H2 italic={false}>{t(texts.newCompany.summary.heading)}</Heading.H2>

				<Stack gap={8}>
					<Stack gap={4}>
						<Anchor as={Link} to="/dashboard">
							<Stack gap={2} direction="row">
								<SvgIcon imageSrc="CHEVRON_LEFT" height={14} />
								{t(texts.newCompany.summary.returnLink)}
							</Stack>
						</Anchor>

						<Heading.H3>{companyName}</Heading.H3>

						{organisationInfo?.orgNr && (
							<Stack direction="row" gap={1}>
								<Text bold className="text-lg">
									{t(texts.newCompany.companyInfo.orgNumber)}
								</Text>
								<Text>{formatOrgOrPersonNumber(organisationInfo.orgNr)}</Text>
							</Stack>
						)}

						{!organisationInfo?.orgNr && <P>{t(texts.newCompany.summary.bodyText)}</P>}
					</Stack>
					<Grid>
						<Grid.Col
							span={{
								base: 12,
								tablet: 8,
							}}
						>
							<Stack gap={8}>
								<Stack gap={4}>
									<Heading.H4>{t(texts.newCompany.companyInfo.heading)}</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyInfo.addressHeading)}
											</Text>
											{!!companyRegistration?.companyAddress?.coAddress && (
												<Text>
													{`${t(texts.newCompany.companyInfo.coAddress)}: ${companyRegistration?.companyAddress?.coAddress}`}
												</Text>
											)}
											<Text>
												{`${t(texts.newCompany.companyInfo.address)}: ${companyRegistration?.companyAddress?.addressLine}`}
											</Text>
											<Text>
												{`${t(texts.newCompany.companyInfo.zip)}: ${companyRegistration?.companyAddress?.zipCode}`}
											</Text>
											<Text>
												{`${t(texts.newCompany.companyInfo.city)}: ${companyRegistration?.companyAddress?.postalCity}`}
											</Text>
										</Stack>
									</Card>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyInfo.contactHeading)}
											</Text>
											<Text>{`${applicant.firstName} ${applicant.lastName}`}</Text>
											<Text>{`${applicant.email}`}</Text>
											<Text>{`${applicant.phone?.countryCode} ${applicant.phone?.number}`}</Text>
										</Stack>
									</Card>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyInfo.namesHeading)}
											</Text>
											{companyRegistration?.proposedCompanyNames.map((name, index) => (
												<Text key={name}>{`${index + 1}. ${name}`}</Text>
											))}
										</Stack>
									</Card>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyInfo.businessDescription)}
											</Text>
											<Text>{companyRegistration?.businessDescription}</Text>
										</Stack>
									</Card>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyInfo.sni)}
											</Text>
											<Stack>
												{companyRegistration?.sniCodes.map((code) => {
													const { description = "", sniCode = "" } =
														allSniCodes.find((c) => c.sniCode === code) || {};
													return (
														<Text key={code}>{`${formatSniCode(sniCode)} ${description}`}</Text>
													);
												})}
											</Stack>
										</Stack>
									</Card>
								</Stack>
								<Stack gap={4}>
									<Heading.H4>{t(texts.newCompany.shares.title)}</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack>
											<Text>
												{`${t(texts.newCompany.shares.capital)}: ${formatNumericValue(companyShares.value?.shareCapital)}`}
											</Text>
											<Text>
												{`${t(texts.newCompany.shares.numberOfShares)}: ${formatNumericValue(getTotalDistributedShares(companyShares.value?.shareholders))}`}
											</Text>
											<Text>
												{`${t(texts.newCompany.shares.sharePrice)}: ${formatNumericValue((companyShares.value?.shareCapital || 0) / (getTotalDistributedShares(companyShares.value?.shareholders) || 1))}`}
											</Text>
										</Stack>
									</Card>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack gap={4}>
											<Text bold className="text-lg">
												{t(texts.newCompany.shares.shareholdersHeading)}
											</Text>
											{companyShares.value?.shareholders.map((s) => (
												<Stack key={s.nationalId}>
													<P>
														<Text bold>{`${s.firstName} ${s.lastName}`}</Text>
													</P>
													<Text>{formatSsn(s.nationalId)}</Text>
													<Text>{`${t(texts.newCompany.shares.sharesNumber, formatNumericValue(s.numberOfShares))}`}</Text>
												</Stack>
											))}
										</Stack>
									</Card>
								</Stack>
								<Stack gap={4}>
									<Heading.H4>{t(texts.newCompany.beneficialOwnership.title)}</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack gap={4}>
											{beneficialOwners.value?.beneficialOwners.map((b) => (
												<Stack gap={4} key={b.nationalId}>
													<Stack>
														<P>
															<Text bold>{`${b.firstName} ${b.lastName}`}</Text>
														</P>
														<Text>{formatSsn(b.nationalId)}</Text>
														{getIsPersonShareholder(
															b.nationalId,
															companyShares.value?.shareholders,
														) && <Text>{t(texts.newCompany.shareholder)}</Text>}
													</Stack>
													<Stack>
														<Text bold>
															{t(texts.newCompany.beneficialOwnership.controlHeading)}
														</Text>
														<Ul>
															{b.controlTypes.map((c) => {
																const description = controlTypeMap.find(
																	(cm) => cm.value === c,
																)?.label;
																return description ? <Li key={c}>{description}</Li> : null;
															})}
														</Ul>
													</Stack>
													<Stack>
														<Text bold>
															{t(texts.newCompany.beneficialOwnership.controlRangeHeading)}
														</Text>
														<Text>
															{b.ownershipPercentMin === 100
																? "100%"
																: t(
																		texts.newCompany.beneficialOwnership.controlRangeDescription,
																		b.ownershipPercentMin.toString(),
																		b.ownershipPercentMax.toString(),
																	)}
														</Text>
													</Stack>
												</Stack>
											))}
										</Stack>
									</Card>
								</Stack>
								<Stack gap={4}>
									<Heading.H4>{t(texts.newCompany.companyStructure.title)}</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack gap={4}>
											<Text bold className="text-lg">
												{t(texts.newCompany.companyStructure.representativesTitle)}
											</Text>
											{representatives.value?.representatives.map((r) => (
												<Stack key={r.nationalId}>
													<P>
														<Text bold>{`${r.firstName} ${r.lastName}`}</Text>
													</P>
													<Text>{formatSsn(r.nationalId)}</Text>
													{getIsPersonShareholder(
														r.nationalId,
														companyShares.value?.shareholders,
													) && <Text>{t(texts.newCompany.shareholder)}</Text>}
													{r.functions?.map((f) => {
														const description = roles.find((r) => r.value === f)?.label;
														return description ? <Text key={f}>{description}</Text> : null;
													})}
												</Stack>
											))}
										</Stack>
									</Card>
								</Stack>
								<Stack gap={4}>
									<Heading.H4>
										{t(texts.newCompany.companyStructure.signatoryCombinationTitle)}
									</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Text>{signatoryCombinationDescription}</Text>
									</Card>
								</Stack>
								<Stack gap={4}>
									<Heading.H4>{t(texts.newCompany.agreement.packageAndCardsHeading)}</Heading.H4>
									<Card classNames="bg-background-tertiary" fullWidth>
										<Stack gap={4}>
											<Text bold className="text-lg">
												{t(text.title)}
											</Text>
											<Markdown>{t(text.summary.packageProductBullets)}</Markdown>
											<Divider style="dashed" className="border-foreground-info" height={1} />
											<ClcCardSummary cardHolderNationalId={applicant.nationalId} />
											<Divider style="dashed" className="border-foreground-info" height={1} />
											<Text bold align="end">
												{t(
													text.summary.packageMonthlyCost,
													moneyFormatNoDecimal(Number(selectedPackage.monthlyFee.amount)),
												)}
											</Text>
										</Stack>
									</Card>
									{selectedPackage.products.filter((p) => p.isAddon).length > 0 && (
										<Card classNames="bg-background-tertiary" fullWidth>
											<Stack gap={2}>
												<Text className="text-lg" bold>
													{t(text.summary.extraDebitCards)}
												</Text>
												<Divider style="dashed" className="border-foreground-info" height={1} />
												{selectedPackage.products
													.filter((p) => p.isAddon)
													.map((p) => cards.find((c) => c.productId === p.productId))
													.filter((c) => !!c && c.isConfigured)
													.map((c) => (
														<Stack gap={4} key={c.productId}>
															<ClcCardSummary cardHolderNationalId={c.nationalId} />
															<Divider
																style="dashed"
																className="border-foreground-info"
																height={1}
															/>
														</Stack>
													))}
												<Text align="end" bold>
													{t(
														text.summary.extraDebitCardsTotalCost,
														getTotalAddonCost(selectedPackage).toString(),
													)}
												</Text>
											</Stack>
										</Card>
									)}
								</Stack>
							</Stack>
						</Grid.Col>
					</Grid>
					<Anchor as={Link} to="/dashboard">
						<Stack gap={2} direction="row">
							<SvgIcon imageSrc="CHEVRON_LEFT" height={14} />
							{t(texts.newCompany.summary.returnLink)}
						</Stack>
					</Anchor>
				</Stack>
			</Stack>
		</Content>
	);
};

export default withLoader(useClcCompanyApplication, ClcSummaryPage);
